import { useState, useEffect } from 'react';
import { TriageData, TriageOutcome, AIInsight } from '@/types/triage';
import { explainTriage } from '@/utils/manchesterTriageRules';
import { triageRulesEngine } from '@/utils/triageRules';
import { aiService } from '@/services/aiService';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { TriageSubmission } from '@/types/database';

export function useTriageAssessment() {
  const [isAssessing, setIsAssessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const { profile } = useAuth();

  const assessTriage = async (triageData: TriageData): Promise<TriageOutcome & { savedTriageId?: string }> => {
    setIsAssessing(true);
    
    try {
      // Simulate AI processing time
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Use Manchester Triage System's explainTriage to get explanation with extracted symptoms
      const triageExplanation = explainTriage(triageData);
      console.log('Triage explanation with extracted symptoms:', triageExplanation);
      
      // Apply clinical rules - use the processed data with extracted symptoms
      // Create a copy of triageData with the processed symptoms from explanation
      const processedTriageData = {
        ...triageData,
        symptoms: triageExplanation.processedSymptoms || triageData.symptoms
      };
      
      const rulesOutcome = triageRulesEngine.assess(processedTriageData);
      
      // Combine the explanation with the rules outcome
      const combinedOutcome = {
        ...rulesOutcome,
        presentingComplaint: triageExplanation.presentingComplaint || rulesOutcome.presentingComplaint,
        discriminator: triageExplanation.discriminator || rulesOutcome.discriminator,
        explanation: triageExplanation,
        processedSymptoms: triageExplanation.processedSymptoms,
        symptomsWithSources: triageExplanation.symptomsWithSources
      };
      
      // Simulate AI enhancement (in real implementation, this would call GPT)
      const aiEnhancedOutcome = await enhanceWithAI(processedTriageData, combinedOutcome);
      
      console.log('Triage assessment completed with extracted symptoms:', aiEnhancedOutcome);
      
      // Save triage data to the database
      const savedTriageId = await saveTriageData(triageData, aiEnhancedOutcome);
      
      // Return the outcome with the saved triage ID
      return {
        ...aiEnhancedOutcome,
        savedTriageId
      };
    } catch (error) {
      console.error('Triage assessment error:', error);
      toast({
        title: "Assessment Error",
        description: "There was an error processing your assessment. Please try again.",
        variant: "destructive",
      });
      
      // Return a safe default assessment
      return {
        urgency: 'standard',
        manchesterCategory: 'Orange - Standard Priority',
        presentingComplaint: 'Error',
        discriminator: 'System Error',
        recommendation: 'Please consult with a healthcare provider within 24 hours.',
        redFlags: [],
        clinicalNotes: 'Assessment could not be completed due to technical error.',
        nextSteps: ['Contact your healthcare provider', 'Monitor symptoms'],
        aiConfidence: 0,
        processedSymptoms: triageData.symptoms || [],
        symptomsWithSources: (triageData.symptoms || []).map(s => ({ name: s, source: 'explicit' }))
      };
    } finally {
      setIsAssessing(false);
    }
  };

  // Function to save triage data to the database
  const saveTriageData = async (triageData: TriageData, outcome: TriageOutcome): Promise<string | undefined> => {
    if (!profile?.id || !profile?.clinic_id) {
      console.error('Cannot save triage data: User or clinic ID is missing');
      return;
    }
    
    // Determine the appropriate patient_id based on user role and provided data
    let effectivePatientId = triageData.patientId;
    
    // If the user is a patient, use their own ID
    if (profile.role === 'patient') {
      effectivePatientId = profile.id;
      console.log('Using authenticated patient ID:', effectivePatientId);
    }
    
    // If still no patient ID and user is not a patient (e.g., clinician creating triage)
    if (!effectivePatientId) {
      console.error('Cannot save triage data: Patient ID is required but missing');
      toast({
        title: "Missing Patient ID",
        description: "A patient must be selected to save triage data.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    console.log('Saving triage data to database...');

    try {
      // Prepare the data for saving
      // Ensure required fields are present and properly formatted
      const triageSubmission = {
        // Required fields per database schema
        patient_id: effectivePatientId, // Using the determined effective patient ID
        clinic_id: profile.clinic_id, // Required field per schema
        // Use reviewed_by instead of clinician_id to track the clinician
        reviewed_by: profile.id, // Store the clinician ID in the reviewed_by field
        reviewed_at: new Date().toISOString(), // Set the review timestamp
        symptoms: triageData.symptoms,
        severity: triageData.severity,
        duration: triageData.duration,
        onset: triageData.onset,
        additional_info: triageData.additionalInfo,
        vitals: {
          temperature: triageData.vitals?.temperature,
          heart_rate: triageData.vitals?.heartRate,
          blood_pressure: triageData.vitals?.bloodPressure,
          respiratory_rate: triageData.vitals?.respiratoryRate,
          oxygen_saturation: triageData.vitals?.oxygenSaturation,
        },
        age: triageData.age,
        gender: triageData.gender,
        is_pregnant: triageData.isPregnant,
        smoking_status: triageData.smokingStatus,
        medical_history: triageData.medicalHistory,
        medications: triageData.medications,
        allergies: triageData.allergies,
        urgency: outcome.urgency,
        manchester_category: outcome.manchesterCategory,
        recommendation: outcome.recommendation,
        red_flags: outcome.redFlags,
        clinical_notes: outcome.clinicalNotes,
        next_steps: outcome.nextSteps,
        estimated_wait_time: outcome.estimatedWaitTime,
        ai_confidence: outcome.aiConfidence,
        // Ensure processed_symptoms is properly formatted for PostgreSQL
        processed_symptoms: Array.isArray(outcome.processedSymptoms) ? outcome.processedSymptoms : null,
        // Ensure symptoms_with_sources is properly formatted for PostgreSQL JSONB
        // Try different serialization approach based on previous memory
        symptoms_with_sources: outcome.symptomsWithSources ? JSON.parse(JSON.stringify(outcome.symptomsWithSources)) : null,
        status: 'submitted', // Initial status (matching the database enum)
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Enhanced debug logging to check the data format
      console.log('DETAILED_DEBUG: Complete triageSubmission object:', JSON.stringify(triageSubmission, null, 2));
      
      console.log('DETAILED_DEBUG: Key fields analysis:', {
        processedSymptoms: {
          value: triageSubmission.processed_symptoms,
          type: typeof triageSubmission.processed_symptoms,
          isArray: Array.isArray(triageSubmission.processed_symptoms),
          length: Array.isArray(triageSubmission.processed_symptoms) ? triageSubmission.processed_symptoms.length : 'N/A'
        },
        symptomsWithSources: {
          value: triageSubmission.symptoms_with_sources,
          type: typeof triageSubmission.symptoms_with_sources,
          isArray: Array.isArray(triageSubmission.symptoms_with_sources),
          stringified: JSON.stringify(triageSubmission.symptoms_with_sources)
        },
        patient_id: {
          value: triageSubmission.patient_id,
          type: typeof triageSubmission.patient_id
        },
        clinic_id: {
          value: triageSubmission.clinic_id,
          type: typeof triageSubmission.clinic_id
        },
        reviewed_by: {
          value: triageSubmission.reviewed_by,
          type: typeof triageSubmission.reviewed_by
        }
      });

      // Insert data into the triage_submissions table
      const { data, error } = await supabase
        .from('triage_submissions')
        .insert(triageSubmission)
        .select(); // Add select() to return the inserted data

      if (error) {
        console.error('Error saving triage data:', error);
        console.error('DETAILED_ERROR: Complete submission object that caused error:', JSON.stringify(triageSubmission, null, 2));
        
        // Log specific fields that might be causing issues
        console.error('DETAILED_ERROR: Key fields analysis:', {
          patient_id: {
            value: triageSubmission.patient_id,
            type: typeof triageSubmission.patient_id
          },
          clinic_id: {
            value: triageSubmission.clinic_id,
            type: typeof triageSubmission.clinic_id
          },
          reviewed_by: {
            value: triageSubmission.reviewed_by,
            type: typeof triageSubmission.reviewed_by
          },
          symptoms_with_sources: {
            value: triageSubmission.symptoms_with_sources,
            type: typeof triageSubmission.symptoms_with_sources,
            stringified: JSON.stringify(triageSubmission.symptoms_with_sources)
          },
          processed_symptoms: {
            value: triageSubmission.processed_symptoms,
            type: typeof triageSubmission.processed_symptoms,
            isArray: Array.isArray(triageSubmission.processed_symptoms)
          },
          status: triageSubmission.status,
          urgency: triageSubmission.urgency
        });
        toast({
          title: "Save Error",
          description: "There was an error saving your assessment data.",
          variant: "destructive",
        });
        return undefined;
      } else {
        console.log('Triage data saved successfully:', data);
        toast({
          title: "Assessment Saved",
          description: "Your health assessment has been successfully saved.",
          variant: "default",
        });
        
        // Return the saved triage ID
        const savedId = data && Array.isArray(data) && data.length > 0 ? 
          (data[0] as TriageSubmission).id : undefined;
        console.log('Returning saved triage ID:', savedId);
        return savedId;
      }
    } catch (error) {
      console.error('Exception saving triage data:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    assessTriage,
    isAssessing,
    isSaving,
    saveTriageData
  };
}

/**
 * Enhance triage outcome with AI insights using the AIService
 * This preserves the existing functionality while using the new centralized service
 */
async function enhanceWithAI(triageData: TriageData, rulesOutcome: TriageOutcome): Promise<TriageOutcome> {
  console.log('[useTriageAssessment] Enhancing triage outcome with AI insights');
  
  try {
    // Get AI insights from the AIService
    const aiInsights = await aiService.generateAIInsights(triageData, rulesOutcome);
    
    // Ensure we preserve the symptom extraction data
    return {
      ...rulesOutcome,
      clinicalNotes: `${rulesOutcome.clinicalNotes} ${aiInsights.additionalNotes}`,
      aiConfidence: aiInsights.confidence,
      nextSteps: [...rulesOutcome.nextSteps, ...aiInsights.additionalSteps],
      redFlags: [...rulesOutcome.redFlags, ...aiInsights.redFlags],
      // Explicitly preserve these fields to ensure they're not lost
      processedSymptoms: rulesOutcome.processedSymptoms,
      symptomsWithSources: rulesOutcome.symptomsWithSources,
      explanation: rulesOutcome.explanation
    };
  } catch (error) {
    console.error('[useTriageAssessment] Error enhancing triage with AI:', error);
    // Return the original outcome without AI enhancement in case of error
    return rulesOutcome;
  }
}

// The generateAIInsights function has been moved to src/services/aiService.ts
// This improves code organization and allows for better modularization
// The functionality remains the same, but is now accessible through aiService.generateAIInsights()
